import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Download,
  LayoutGrid,
  List,
  BarChart3,
  Map,
  ArrowLeft
} from 'lucide-react';
import Header from '../../../../components/header/Header';
import Footer from '../../../../components/footer/Footer';
import HeroCarousel from '../../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../../shared/components/Breadcrumb';
import img1 from '../../../../assets/attendenceCarousel.png';
import {
  SummaryCards,
  RegionStateFilter,
  DateRangePicker,
  KVTable
} from '../../components/analytics';
import { attendanceApi } from '../../services/api';
import type { SortField, TableSortConfig, ViewMode, AnalyticsFilters, KVAttendanceSummary } from '../../types/analytics';

// Server-side pagination – 50 records per page


const filterKVList = (list: KVAttendanceSummary[], opts: { searchQuery?: string }) => {
  let filtered = [...list];

  if (opts.searchQuery && opts.searchQuery.trim().length > 0) {
    const q = opts.searchQuery.trim().toLowerCase();
    filtered = filtered.filter(kv =>
      (kv.kvName || '').toLowerCase().includes(q) ||
      (kv.roName || '').toLowerCase().includes(q) ||
      (kv.stateName || '').toLowerCase().includes(q) ||
      (kv.districtName || '').toLowerCase().includes(q) ||
      String(kv.kvId || '').includes(q)
    );
  }

  return filtered;
};

// Generate overall summary from the filtered KV list
const generateSummary = (list: KVAttendanceSummary[]) => {
  const totalKVs = list.length;
  const totalRegularStaff = list.reduce((s, kv) => s + (kv.regularStaff || 0), 0);
  const totalContractualStaff = list.reduce((s, kv) => s + (kv.contractualStaff || 0), 0);
  const totalStaff = totalRegularStaff + totalContractualStaff;
  const totalPresent = list.reduce((s, kv) => s + (kv.presentCount || 0), 0);
  const totalAbsent = list.reduce((s, kv) => s + (kv.absentCount || 0), 0);
  const totalNoResponse = list.reduce((s, kv) => s + (kv.noResponseCount || 0), 0);

  // Weighted attendance for regular/contractual based on counts and percentages (approximate)
  const totalRegularPresent = list.reduce((s, kv) => s + ((kv.regularStaff || 0) * (((kv as any).regularPercent || 0) / 100)), 0);
  const totalContractualPresent = list.reduce((s, kv) => s + ((kv.contractualStaff || 0) * (((kv as any).contractualPercent || 0) / 100)), 0);

  const overallAttendancePercent = totalStaff > 0 ? Math.round((totalPresent / totalStaff) * 100 * 10) / 10 : 0;
  const regularAttendancePercent = totalRegularStaff > 0 ? Math.round((totalRegularPresent / totalRegularStaff) * 100 * 10) / 10 : 0;
  const contractualAttendancePercent = totalContractualStaff > 0 ? Math.round((totalContractualPresent / totalContractualStaff) * 100 * 10) / 10 : 0;

  return {
    totalKVs,
    totalStaff,
    totalRegularStaff,
    totalContractualStaff,
    totalPresent,
    totalAbsent,
    totalNoResponse,
    overallAttendancePercent,
    regularAttendancePercent,
    contractualAttendancePercent
  };
};


// Transform API response to KVAttendanceSummary format
// Latest API fields:
//   Regular:     kvSumOftotalPresentReg, kvSumOftotalEnrollmentReg, kvPresentPercentageReg
//   Contractual: kvSumOftotalPresentRegContractual, kvSumOftotalEnrollmentRegContractual, kvPresentPercentageRegContractual
const transformPaginatedAPIDataToKVList = (apiData: any[]): KVAttendanceSummary[] => {
  return apiData.map((item: any, index: number) => {
    // --- Regular staff aggregated counts ---
    const totalReg = parseInt(item.kvSumOftotalEnrollmentReg) || 0;
    const presentReg = parseInt(item.kvSumOftotalPresentReg) || 0;
    const absentReg = totalReg - presentReg;
    const presentPctReg = parseFloat(item.kvPresentPercentageReg) || 0;

    // --- Contractual staff aggregated counts ---
    const totalCon = parseInt(item.kvSumOftotalEnrollmentRegContractual) || 0;
    const presentCon = parseInt(item.kvSumOftotalPresentRegContractual) || 0;
    const absentCon = totalCon - presentCon;
    const presentPctCon = parseFloat(item.kvPresentPercentageRegContractual) || 0;

    // Combined totals
    const totalStaff = totalReg + totalCon;
    const totalPresent = presentReg + presentCon;
    const totalAbsent = absentReg + absentCon;
    // Weighted combined attendance %
    const attendancePercent = totalStaff > 0
      ? Math.round(((totalPresent / totalStaff) * 100) * 10) / 10
      : 0;

    // School name: API uses 'schoolName' as primary field
    const schoolName = item.schoolName || item.kvSchoolNameMaster || '';
    // Shift info
    const shiftLabel = item.kvShift === 1 ? 'Shift - 1' : item.kvShift === 2 ? 'Shift - 2' : '';
    const displayName = shiftLabel ? `${schoolName} (${shiftLabel})` : schoolName;

    // State: API uses 'kvStateName'
    const stateName = item.kvStateName || item.kvStateNameMaster || item.stateName || '';

    return {
      kvId: item.kvID || item.kvMasterId || item.schoolID || index + 1,
      kvCode: item.kvCodeMaster || item.kvCode || `KV${index + 1}`,
      kvName: displayName,
      roId: item.roID || item.kvRoIDMaster || 0,
      roName: item.roName || item.kvRoNameMaster || '',
      stateId: item.stateID || item.state_id || 0,
      stateName,
      districtId: item.districtId || 0,
      districtName: item.districtName || '',
      totalStaff,
      regularStaff: totalReg,
      contractualStaff: totalCon,
      presentCount: totalPresent,
      absentCount: totalAbsent,
      noResponseCount: 0,
      attendancePercent,
      regularPercent: Math.round(presentPctReg * 10) / 10,
      contractualPercent: Math.round(presentPctCon * 10) / 10
    };
  });
};

const KVAttendanceList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const carouselImages = [img1];

  // Get roId and roName from URL params
  const roIdParam = searchParams.get('roId');
  const roId = roIdParam ? Number(roIdParam) : undefined;
  const roNameParam = searchParams.get('roName') || '';

  // API data state
  const [kvList, setKvList] = useState<KVAttendanceSummary[]>([]);
  const [roName, setRoName] = useState<string>(roNameParam);
  // API-provided overall summary (from response.summary.overall)
  const [apiOverallSummary, setApiOverallSummary] = useState<{
    sumOftotalPresentReg: number;
    sumOftotalEnrollmentReg: number;
    presentPercentageReg: number;
    sumOftotalPresentCon: number;
    sumOftotalEnrollmentCon: number;
    presentPercentageCon: number;
  } | null>(null);

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalServerPages, setTotalServerPages] = useState(1);
  const [totalServerRecords, setTotalServerRecords] = useState(0);
  const PAGE_SIZE = 50;

  // Filters state
  const [filters, setFilters] = useState<AnalyticsFilters>(() => {
    const regionId = searchParams.get('region');
    const stateId = searchParams.get('state');

    return {
      regionId: regionId ? Number(regionId) : undefined,
      stateId: stateId ? Number(stateId) : undefined,
    };
  });

  // Date range state (from URL or default to last 30 days)
  const [dateRange, setDateRange] = useState<{ fromDate: string; toDate: string }>(() => {
    const fromParam = searchParams.get('fromDate');
    const toParam = searchParams.get('toDate');
    
    if (fromParam && toParam) {
      return { fromDate: fromParam, toDate: toParam };
    }
    
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      fromDate: thirtyDaysAgo.toISOString().split('T')[0],
      toDate: today.toISOString().split('T')[0]
    };
  });

  // Local state
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortConfig, setSortConfig] = useState<TableSortConfig>({
    field: 'attendancePercent',
    order: 'desc'
  });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch a specific page from server
  const fetchPage = useCallback(async (page: number) => {
    setIsLoading(true);

    try {
      const response = await attendanceApi.getPaginatedDailyAttendanceByKVS(
        page,
        dateRange.fromDate,
        dateRange.toDate,
        roId,
        'presentPercentageReg',
        'DESC'
      );

      if (response && response.data) {
        const transformedData = transformPaginatedAPIDataToKVList(response.data);
        setKvList(transformedData);
        setCurrentPage(response.pagination?.page || page);
        setTotalServerPages(response.pagination?.totalPages || 1);
        setTotalServerRecords(response.pagination?.totalRecords || transformedData.length);

        // Capture the server-provided overall summary (response.summary.overall)
        if (response.summary?.overall) {
          setApiOverallSummary(response.summary.overall);
        } else {
          setApiOverallSummary(null);
        }

        // Set RO name from first record if available
        if (transformedData.length > 0 && roId) {
          setRoName(transformedData[0].roName);
        }
      } else {
        setKvList([]);
        setTotalServerPages(1);
        setTotalServerRecords(0);
        setApiOverallSummary(null);
      }
    } catch (err) {
      console.error('Error fetching KV attendance data:', err);
      setKvList([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, roId]);

  // Fetch data (always reset to page 1 on filter/date change)
  const fetchData = useCallback(async () => {
    setCurrentPage(1);
    await fetchPage(1);
  }, [fetchPage]);

  // Load data on mount and when date/filter changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle server-side page navigation
  const handlePageChange = useCallback((page: number) => {
    if (page < 1 || page > totalServerPages || page === currentPage) return;
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, totalServerPages, fetchPage]);

  // Update filter helper
  const updateFilter = useCallback(<K extends keyof AnalyticsFilters>(key: K, value: AnalyticsFilters[K]) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : (value as number)
    }));
  }, []);

  // Reset filters
  const resetFilters = useCallback(() => {
    setFilters({});
    setSearchQuery('');
  }, []);

  // Filtered and sorted data (client-side within the current page)
  const { filteredList, summary, totalRecords } = useMemo(() => {
    // Apply client-side search filter
    const filtered = filterKVList(kvList, { searchQuery });

    // Sort client-side within the current page
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[sortConfig.field] ?? '';
      const bValue = b[sortConfig.field] ?? '';

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.order === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      return sortConfig.order === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    // Build the OverallSummary object.
    // Prefer the server-provided summary.overall (covers ALL pages / full date range).
    // Fall back to computing from the current page records when unavailable.
    let summaryData;
    if (apiOverallSummary) {
      const totalReg = apiOverallSummary.sumOftotalEnrollmentReg;
      const presentReg = apiOverallSummary.sumOftotalPresentReg;
      const totalCon = apiOverallSummary.sumOftotalEnrollmentCon;
      const presentCon = apiOverallSummary.sumOftotalPresentCon;
      const totalStaff = totalReg + totalCon;
      const totalPresent = presentReg + presentCon;
      const totalAbsent = totalStaff - totalPresent;
      const overallAttendancePercent = totalStaff > 0
        ? Math.round((totalPresent / totalStaff) * 100 * 10) / 10
        : 0;
      summaryData = {
        totalKVs: totalServerRecords,
        totalStaff,
        totalRegularStaff: totalReg,
        totalContractualStaff: totalCon,
        totalPresent,
        totalAbsent,
        totalNoResponse: 0,
        overallAttendancePercent,
        regularAttendancePercent: Math.round(apiOverallSummary.presentPercentageReg * 10) / 10,
        contractualAttendancePercent: Math.round(apiOverallSummary.presentPercentageCon * 10) / 10,
      };
    } else {
      summaryData = generateSummary(sorted);
    }

    return {
      filteredList: sorted,
      summary: summaryData,
      totalRecords: totalServerRecords // Use server total for display
    };
  }, [kvList, searchQuery, sortConfig, totalServerRecords, apiOverallSummary]);

  // Derived lists for filters (regions & states) from the KV list
  const regions = useMemo(() => {
    const seen = new Set<number>();
    const out: { roId: number; roName: string }[] = [];
    kvList.forEach(kv => {
      if (kv.roId && !seen.has(kv.roId)) {
        seen.add(kv.roId);
        out.push({ roId: kv.roId, roName: kv.roName });
      }
    });
    return out;
  }, [kvList]);

  const states = useMemo(() => {
    const seen = new Set<number>();
    const out: { stateId: number; stateName: string }[] = [];
    kvList.forEach(kv => {
      if (kv.stateId && !seen.has(kv.stateId)) {
        seen.add(kv.stateId);
        out.push({ stateId: kv.stateId, stateName: kv.stateName });
      }
    });
    return out;
  }, [kvList]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (roId) params.set('roId', String(roId));
    if (roNameParam) params.set('roName', roNameParam);
    if (dateRange.fromDate) params.set('fromDate', dateRange.fromDate);
    if (dateRange.toDate) params.set('toDate', dateRange.toDate);
    if (filters.regionId) params.set('region', String(filters.regionId));
    if (filters.stateId) params.set('state', String(filters.stateId));

    setSearchParams(params, { replace: true });
  }, [roId, roNameParam, dateRange, filters, setSearchParams]);

  // Handle date range change
  const handleDateRangeChange = useCallback((fromDate: string, toDate: string) => {
    setDateRange({ fromDate, toDate });
  }, []);

  // Handle page change

  // Handle sort
  const handleSort = useCallback((field: SortField) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // Handle export
  const handleExport = useCallback(() => {
    if (!filteredList.length) return;

    const headers = ['#', 'KV Name', 'Region', 'State', 'District', 'Total Staff', 'Regular', 'Contractual', 'Present', 'Absent', 'No Response', 'Attendance %'];
    const rows = filteredList.map((kv, idx) => [
      idx + 1,
      kv.kvName,
      kv.roName,
      kv.stateName,
      kv.districtName,
      kv.totalStaff,
      kv.regularStaff,
      kv.contractualStaff,
      kv.presentCount,
      kv.absentCount,
      kv.noResponseCount,
      kv.attendancePercent
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kv-attendance-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredList]);

  // Handle KV row click - pass KV info (kvName, roId, roName, stateId, stateName) via URL params
  const handleKVClick = useCallback((kv: any) => {
    const params = new URLSearchParams();
    params.append('fromDate', dateRange.fromDate);
    params.append('toDate', dateRange.toDate);
    if (kv.kvName) params.append('kvName', kv.kvName);
    if (kv.roId) params.append('roId', String(kv.roId));
    if (kv.roName) params.append('roName', kv.roName);
    if (kv.stateId) params.append('stateId', String(kv.stateId));
    if (kv.stateName) params.append('stateName', kv.stateName);
    if (kv.districtName) params.append('districtName', kv.districtName);

    navigate(`/attendance/analytics/kv/${kv.kvId}?${params.toString()}`);
  }, [dateRange, navigate]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate('/attendance/analytics');
  }, [navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {roId && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-white hover:shadow rounded-lg transition-all"
                title="Back to Regions"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-[#6A1786]">
                  {roId ? `${roName || 'Region'} - KV Schools` : 'KV Attendance Analytics'}
                </h1>
                {roId && (
                  <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                    RO ID: {roId}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {totalRecords.toLocaleString()} KV records • {totalServerPages} pages
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExport}
              disabled={!filteredList.length}
              className="p-2 hover:bg-white hover:shadow rounded-lg transition-all disabled:opacity-50"
              title="Export to CSV"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <SummaryCards summary={summary} isLoading={isLoading} />

        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search KV schools..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6A1786] focus:border-[#6A1786]"
              />
            </div>

            {/* Date Range Picker */}
            <DateRangePicker
              value={{ fromDate: dateRange.fromDate, toDate: dateRange.toDate }}
              onSubmit={(range) => {
                if (range.fromDate && range.toDate) {
                  handleDateRangeChange(range.fromDate, range.toDate);
                }
              }}
              disabled={isLoading}
            />

            {/* Region/State Filters - only show if no roId */}
            {!roId && (
              <RegionStateFilter
                filters={filters}
                regions={regions}
                states={states}
                onFilterChange={updateFilter}
                onReset={resetFilters}
                disabled={isLoading}
              />
            )}

            {/* View Mode Toggle */}
            {/* <div className="flex items-center bg-gray-100 rounded-lg p-1 ml-auto">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow text-[#6A1786]' : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="Table view"
              >
                <List className="w-4 h-4" />
              </button> */}
              {/* <button
                onClick={() => setViewMode('chart')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'chart' ? 'bg-white shadow text-[#6A1786]' : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="Chart view"
              >
                <BarChart3 className="w-4 h-4" />
              </button> */}
              {/* <button
                onClick={() => setViewMode('heatmap')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'heatmap' ? 'bg-white shadow text-[#6A1786]' : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="Heatmap view"
              > */}
                {/* <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('map')}
                className={`p-2 rounded-md transition-colors ${viewMode === 'map' ? 'bg-white shadow text-[#6A1786]' : 'text-gray-500 hover:text-gray-700'
                  }`}
                title="Map view"
              >
                <Map className="w-4 h-4" />
              </button> */}
            {/* </div> */}
          </div>
        </div>

        {/* Content based on view mode */}
        {viewMode === 'table' && (
          <>
            <KVTable
              data={filteredList}
              isLoading={isLoading}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowClick={handleKVClick}
            />

            {/* Server-Side Pagination */}
            {totalServerPages > 1 && (
              <div className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-gray-500">
                  Page <span className="font-medium text-gray-900">{currentPage}</span> of{' '}
                  <span className="font-medium text-gray-900">{totalServerPages}</span>{' '}
                  &bull; {totalServerRecords.toLocaleString()} total records
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1 || isLoading}
                    className="px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                  >
                    «
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || isLoading}
                    className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                  >
                    ‹ Prev
                  </button>
                  {/* Page number buttons */}
                  {(() => {
                    const pages: number[] = [];
                    const start = Math.max(1, currentPage - 2);
                    const end = Math.min(totalServerPages, currentPage + 2);
                    for (let p = start; p <= end; p++) pages.push(p);
                    return pages.map(p => (
                      <button
                        key={p}
                        onClick={() => handlePageChange(p)}
                        disabled={isLoading}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          p === currentPage
                            ? 'bg-[#6A1786] text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {p}
                      </button>
                    ));
                  })()}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalServerPages || isLoading}
                    className="px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                  >
                    Next ›
                  </button>
                  <button
                    onClick={() => handlePageChange(totalServerPages)}
                    disabled={currentPage === totalServerPages || isLoading}
                    className="px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100 disabled:opacity-40 transition-colors"
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* {viewMode === 'chart' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Chart view coming soon</p>
            <p className="text-gray-400 text-sm mt-2">
              Bar charts comparing attendance across regions and states
            </p>
          </div>
        )}

        {viewMode === 'heatmap' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <LayoutGrid className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Heatmap view coming soon</p>
            <p className="text-gray-400 text-sm mt-2">
              State-wise attendance heatmap visualization
            </p>
          </div>
        )}

        {viewMode === 'map' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
            <Map className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Map view coming soon</p>
            <p className="text-gray-400 text-sm mt-2">
              Geographic visualization of KV schools with attendance data
            </p>
          </div>
        )} */}
      </main>

      <Footer />
    </div>
  );
};

export default KVAttendanceList;
